// @flow
import { fetchBidderDataIfNeeded, getAuthToken, getBidderId, LOG_OUT_BIDDER } from './user';
import { getIsStripeCustomer, getStripeCreditCard } from './stripe';
import { getLAPaymentCards, postAddLACard, postAddStripeCard, putAddStripeCard } from '../api/payment';
import { type Address } from '@liveauctioneers/caterwaul-components/types/Address';
import { createSelector } from 'reselect';
import { DISMISS_MODAL } from './modal';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import paymentProviders from '../../enums/paymentProviders';

/* Provider constants */
export const squareAuth = {
    applicationId: 'sq0idp-br7J5G4y40RyokR92-POhg',
    locationId: 'CBASEJyJyp9MxFBh4K22rOezCQkgAQ',
};

/* Action Types */
export const ADD_LA_PAYMENT_CARD_FAIL = 'la/ui/addLAPaymentCard/FAIL';
export const ADD_LA_PAYMENT_CARD_REQUEST = 'la/ui/addLAPaymentCard/REQUEST';
export const ADD_LA_PAYMENT_CARD_SUCCESS = 'la/ui/addLAPaymentCard/SUCCESS';

export const LOAD_LA_PAYMENT_CARD_FAIL = 'la/ui/loadLAPaymentCard/FAIL';
export const LOAD_LA_PAYMENT_CARD_REQUEST = 'la/ui/loadLAPaymentCard/REQUEST';
export const LOAD_LA_PAYMENT_CARD_SUCCESS = 'la/ui/loadLAPaymentCard/SUCCESS';

// reducer
const DEFAULT_STATE = {
    error: false,
    errorMessage: null,
    laPaymentCards: [],
    submitted: false,
    success: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
    errorMessage: ?string,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case ADD_LA_PAYMENT_CARD_REQUEST:
        case LOAD_LA_PAYMENT_CARD_REQUEST:
            return {
                ...state,
                error: false,
                errorMessage: null,
                submitted: true,
                success: false,
            };
        case ADD_LA_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                laPaymentCards: [action.payload],
                submitted: false,
                success: true,
            };
        case LOAD_LA_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                laPaymentCards: action.payload,
                submitted: false,
                success: false,
            };
        case ADD_LA_PAYMENT_CARD_FAIL:
        case LOAD_LA_PAYMENT_CARD_FAIL:
            return {
                ...state,
                error: true,
                errorMessage: action.payload,
                submitted: false,
                success: false,
            };
        case DISMISS_MODAL:
            return {
                ...state,
                error: false,
                errorMessage: null,
                submitted: false,
                success: false,
            };
        case LOG_OUT_BIDDER:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.creditCard;

export const getUiAddCreditCard = createSelector(stateSelector, state => state);

//Just take the default one for now because we don't have any ui for displaying multiple
const lAPaymentCardSelector = createSelector(
    stateSelector,
    state => state.laPaymentCards?.find(card => card.defaultCard) || {}
);

export const getCreditCard = (state: any, paymentProvider: number) => {
    if (paymentProvider === paymentProviders.STRIPE) {
        return getStripeCreditCard(state);
    } else {
        return lAPaymentCardSelector(state);
    }
};

/* ACTION CREATORS */
export type SubmitAddCreditCardParams = {
    address: Address,
    cardholderName: any,
    cardToken: string,
    providerId: number,
};
export const submitAddCreditCard = ({
    address,
    cardholderName,
    cardToken,
    providerId = paymentProviders.STRIPE,
}: SubmitAddCreditCardParams) => async (dispatch: Function) => {
    if (providerId === paymentProviders.STRIPE) {
        return await dispatch(addStripeCreditCard({ cardholderName, cardToken }));
    } else {
        return await dispatch(addLACreditCard({ address, cardholderName, cardToken, providerId }));
    }
};

type AddStripeCreditCardParams = {
    cardholderName: string,
    cardToken: string,
};
export const addStripeCreditCard = ({ cardholderName = '', cardToken }: AddStripeCreditCardParams) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);
        const isStripeCustomer = getIsStripeCustomer(state);

        dispatch({
            payload: bidderId,
            type: ADD_LA_PAYMENT_CARD_REQUEST,
        });

        const apiCall = isStripeCustomer ? putAddStripeCard : postAddStripeCard;
        const response = await apiCall({ authToken, bidderId, cardToken, deployment });
        await dispatch(fetchBidderDataIfNeeded(authToken, true));

        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: ADD_LA_PAYMENT_CARD_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: ADD_LA_PAYMENT_CARD_FAIL,
        });
        throw error;
    }
};

type AddLACreditCardParams = {
    address: Address,
    cardholderName: any,
    cardToken: string,
    providerId: number,
};
export const addLACreditCard = ({ address, cardholderName, cardToken, providerId }: AddLACreditCardParams) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: bidderId,
            type: ADD_LA_PAYMENT_CARD_REQUEST,
        });
        await dispatch(fetchBidderDataIfNeeded(authToken, true));

        // Send the request
        const response = await postAddLACard({
            authToken,
            bidderId,
            billingAddress: address,
            cardholderName: `${cardholderName.first} ${cardholderName.last}`,
            cardNonce: cardToken,
            deployment,
            providerId,
        });
        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.payload,
            type: ADD_LA_PAYMENT_CARD_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: ADD_LA_PAYMENT_CARD_FAIL,
        });
        throw error;
    }
};

export const fetchLAPaymentCardIfNeeded = () => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        dispatch({
            payload: authToken,
            type: LOAD_LA_PAYMENT_CARD_REQUEST,
        });
        const response = await getLAPaymentCards({ authToken, bidderId, deployment });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response.payload,
            type: LOAD_LA_PAYMENT_CARD_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_LA_PAYMENT_CARD_FAIL,
        });
    }
};
